import React, { useEffect, useRef, useState } from 'react'
import { Resource, ResourceFile, ResourceFolder } from '../../utils/interfaces'
import { AddFileIcon, AddFolderIcon, FolderIcon, JavaIcon, Settings } from '../../utils/svgs'
import ResourceFileButton from './ResourceFileButton'
import { useEventListener } from '@iwbam/react-ez'
import Popup from './Popup'
import SelectFolderButton from './SelectFolderButton'
import { getPathFromId } from '../../utils/functions'

interface Props {
  resource: Resource
  folder: ResourceFolder
  showButtonAlways?: boolean
  initialOpen?: boolean
  jarContent?: ArrayBuffer | null
  isRoot?: boolean
  style?: React.CSSProperties
  fontStyle?: React.CSSProperties
  handleMove: (id: string, newDirId: string) => void
  handleDelete: (id: string) => void
  handleRename: (id: string, newName: string) => void
  addFolder: (dirId: string, newFolderName: string) => void
  addFile: (dirId: string, newFileName: string) => void
  openFile: (file: ResourceFile) => void
}

const ResourceFolderButton: React.FC<Props> = (props) => {

  const optionButtonRef = useRef(null);
  const [folder, setFolder] = useState<ResourceFolder>(props.folder);
  const [isOpen, setIsOpen] = useState(props.initialOpen || false);
  const [isOptionOpening, setIsOptionOpening] = useState(false);
  const [optionPos, setOptionPos] = useState({ top: -1000, left: -1000 });
  // rename & delete & move
  const [renameInput, setRenameInput] = useState('');
  const [isFolderRenaming, setIsFolderRenaming] = useState(false);
  const [isFolderMoving, setIsFolderMoving] = useState(false);
  const [newDirId, setNewDirId] = useState('');
  const [isFolderDeleting, setIsFolderDeleting] = useState(false);
  // add new folder or file
  const [newFolderInput, setNewFolderInput] = useState('');
  const [newFileInput, setNewFileInput] = useState('');
  const [isAddingFolder, setIsAddingFolder] = useState(false);
  const [isAddingFile, setIsAddingFile] = useState(false);

  // sort folder initially
  useEffect(() => {
    setFolder({
      ...props.folder, items: props.folder.items.sort((a, b) => {
        if ((a as ResourceFolder).items && !(b as ResourceFolder).items) return -1;
        if (!(a as ResourceFolder).items && (b as ResourceFolder).items) return 1;
        return a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1;
      })
    })
  }, [props.folder]);

  // set position of options
  useEffect(() => {
    if (isOptionOpening && optionButtonRef.current) {
      const btn = optionButtonRef.current as HTMLElement;
      const rect = btn.getBoundingClientRect();
      setOptionPos({ top: rect.top, left: rect.left + rect.width });
    }
  }, [isOptionOpening]);

  // handle click
  useEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (!target.closest(`#z${folder.id}-options`) && !target.closest(`#z${folder.id}-options-btn`)) {
      setIsOptionOpening(false);
    }
  });

  // handle key down
  useEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      if (isFolderRenaming) {
        rename();
      } else if (isAddingFile) {
        addFile();
      } else if (isAddingFolder) {
        addFolder();
      }
    }
  });

  // move folder
  const moveFolder = () => {
    if (!isFolderMoving) return;
    props.handleMove(folder.id, newDirId);
    setIsFolderMoving(false);
    setNewDirId('');
  }

  // delete
  const deleteFolder = () => {
    if (!isFolderDeleting) return;
    props.handleDelete(folder.id);
    setIsFolderDeleting(false);
  }

  // rename
  const rename = () => {
    if (!isFolderRenaming || !renameInput.length) return;
    props.handleRename(folder.id, renameInput);
    setIsFolderRenaming(false);
    setRenameInput('');
  }

  // add file
  const addFile = () => {
    if (!newFileInput.length) return;
    props.addFile(folder.id, newFileInput.includes('.') ? newFileInput : newFileInput + '.java');
    setIsAddingFile(false);
    setNewFileInput('');
    setIsOpen(true);
  }

  // add folder
  const addFolder = () => {
    if (!newFolderInput.length) return;
    props.addFolder(folder.id, newFolderInput);
    setIsAddingFolder(false);
    setNewFolderInput('');
    setIsOpen(true);
  }

  return (
    <>
      <div className={`h-5 px-1 m-0.5 flex items-center justify-between *:hover:flex ${props.isRoot ? '' : 'hover:bg-gray-800 rounded'}`} style={props.style} key={folder.id}>
        {/* name */}
        <div className='flex items-center flex-1 overflow-hidden cursor-pointer' onClick={() => setIsOpen(!isOpen)}>
          {!props.isRoot && <i className={`ml-0.5 flex items-center justify-center w-3 text-xs fa-solid fa-angle-${isOpen ? 'down' : 'right'}`} />}
          {!props.isRoot && <div className={`h-4 p-1`}><FolderIcon /></div>}
          <div className={`text-xs flex-1 overflow-hidden text-ellipsis text-nowrap`} style={props.fontStyle}>{folder.name}</div>
        </div>
        {/* buttons */}
        <div className={`items-center ${props.showButtonAlways || isOptionOpening ? 'flex' : 'hidden'} ml-2`}>
          <button onClick={() => setIsAddingFile(true)} className='p-0.5 flex items-center justify-center h-4 aspect-square rounded-sm hover:bg-gray-800'><AddFileIcon /></button>
          <button onClick={() => setIsAddingFolder(true)} className='p-0.5 flex items-center justify-center h-4 aspect-square rounded-sm hover:bg-gray-800 ml-1.5'><AddFolderIcon /></button>
          <button ref={optionButtonRef} onClick={() => setIsOptionOpening(true)} id={`z${folder.id}-options-btn`} className='p-0.5 flex items-center justify-center h-4 aspect-square rounded-sm hover:bg-gray-800 ml-1.5'><i className='fa-solid fa-ellipsis text-xs' /></button>
        </div>
      </div>
      {isOptionOpening && <div id={`z${folder.id}-options`} className='fixed border flex flex-col rounded overflow-hidden bg-neutral-900 z-10 w-24' style={{ top: optionPos.top, left: optionPos.left }}>
        <button onClick={() => {
          setIsFolderRenaming(true);
          setRenameInput(folder.name);
          setIsOptionOpening(false);
        }} className='text-xs p-1 px-2 text-left hover:bg-gray-800'>Rename</button>
        {!props.isRoot && <>
          <button onClick={() => {
            setIsFolderMoving(true);
            setNewDirId(props.resource.root.id);
            setIsOptionOpening(false);
          }} className='text-xs p-1 px-2 text-left hover:bg-gray-800'>Move</button>
          <button onClick={() => {
            setIsFolderDeleting(true);
            setIsOptionOpening(false);
          }} className='text-xs p-1 px-2 text-left hover:bg-gray-800'>Delete</button>
        </>}
      </div>}
      {/* child files & folders */}
      {isOpen &&
        <>
          <div className='ml-3.5 my-1 border-l'>
            {folder.items.map(item => {
              // return folder button if it's folder else file
              if ((item as ResourceFolder).items) return <ResourceFolderButton resource={props.resource} folder={item as ResourceFolder} handleMove={props.handleMove} handleDelete={props.handleDelete} handleRename={props.handleRename} addFolder={props.addFolder} addFile={props.addFile} openFile={props.openFile} />
              return <ResourceFileButton resource={props.resource} file={item} handleMove={props.handleMove} handleDelete={props.handleDelete} handleRename={props.handleRename} openFile={props.openFile} />
            })}
            {(props.isRoot && props.jarContent) ? <div onClick={() => props.openFile({ id: 'jar', name: `${props.resource.mainClass}.jar` })} className='h-5 text-xs flex items-center m-0.5 p-1 cursor-pointer hover:bg-gray-800 rounded'>
              <div className={`p-1 h-5 w-5 flex items-center justify-center`}><JavaIcon singleColor /></div>
              {props.resource.mainClass}.jar
            </div> : <></>}
            {props.isRoot && <div onClick={() => props.openFile({ id: 'config', name: 'Configuration' })} className='h-5 text-xs flex items-center m-0.5 p-1 cursor-pointer hover:bg-gray-800 rounded'>
              <div className={`h-5 p-1.5 w-5 flex items-center justify-center`}><Settings /></div>
              Configuration
            </div>}
          </div>
        </>}

      {/* popups */}

      {isFolderMoving && <div className='fixed z-50 top-0 left-0 w-screen h-svh flex items-center justify-center'>
        <Popup className='max-w-md move-popup'>
          <div className='font-semibold'>Move folder "<span className='font-bold'>{folder.name}</span>"</div>
          <input className='border rounded cursor-default mt-4 py-1 px-2 text-sm w-full' value={getPathFromId(props.resource.root, newDirId)} readOnly />
          <div className='border rounded text-sm w-full mt-4 h-56 scroller overflow-hidden'>
            <SelectFolderButton
              movingFolderId={folder.id}
              folder={props.resource.root}
              selectedId={newDirId}
              setSelectedId={setNewDirId}
            />
          </div>
          <div className='flex justify-end text-sm mt-8'>
            <button onClick={moveFolder} className='font-bold bg-emerald-500 p-1 px-4 rounded active:bg-emerald-600'>Move</button>
            <button onClick={() => setIsFolderMoving(false)} className='font-bold bg-gray-600 p-1 px-4 rounded ml-4 active:bg-gray-700'>Cancel</button>
          </div>
        </Popup>
      </div>}

      {isFolderDeleting && <div className='fixed z-50 top-0 left-0 w-screen h-svh flex items-center justify-center'>
        <Popup className='max-w-md delete-popup'>
          <div className='font-bold'>Delete folder</div>
          <div className='mt-4 text-sm font-light'>Delete folder <span className='font-bold'>{folder.name}</span>?</div>
          <div className='flex justify-end text-sm mt-8'>
            <button onClick={deleteFolder} className='font-bold bg-red-600 p-1 px-4 rounded active:bg-red-700'>Delete</button>
            <button onClick={() => setIsFolderDeleting(false)} className='font-bold bg-gray-600 p-1 px-4 rounded ml-4 active:bg-gray-700'>Cancel</button>
          </div>
        </Popup>
      </div>}

      {isFolderRenaming && <div className='fixed z-50 top-0 left-0 w-screen h-svh flex items-center justify-center'>
        <Popup className='max-w-md rename-popup'>
          <div className='font-bold'>Rename folder</div>
          <input autoFocus className='border rounded mt-4 py-1 px-2 text-sm w-full' value={renameInput} onInput={(e: React.ChangeEvent<HTMLInputElement>) => setRenameInput(e.target.value)} />
          <div className='flex justify-end text-sm mt-8'>
            <button onClick={rename} className='font-bold bg-emerald-500 p-1 px-4 rounded active:bg-emerald-600 disabled:bg-gray-400' disabled={renameInput.length === 0}>Rename</button>
            <button onClick={() => setIsFolderRenaming(false)} className='font-bold bg-gray-600 p-1 px-4 rounded ml-4 active:bg-gray-700'>Cancel</button>
          </div>
        </Popup>
      </div>}

      {isAddingFile && <div className='fixed z-50 top-0 left-0 w-screen h-svh flex items-center justify-center'>
        <Popup className='max-w-md add-file-popup'>
          <div className='font-bold'>New file</div>
          <input autoFocus className='border rounded mt-4 py-1 px-2 text-sm w-full' value={newFileInput} onInput={(e: React.ChangeEvent<HTMLInputElement>) => setNewFileInput(e.target.value)} />
          <div className='flex justify-end text-sm mt-8'>
            <button onClick={addFile} className='font-bold bg-emerald-500 p-1 px-4 rounded active:bg-emerald-600 disabled:bg-gray-400' disabled={newFileInput.length === 0}>Create</button>
            <button onClick={() => setIsAddingFile(false)} className='font-bold bg-gray-600 p-1 px-4 rounded ml-4 active:bg-gray-700'>Cancel</button>
          </div>
        </Popup>
      </div>}

      {isAddingFolder && <div className='fixed z-50 top-0 left-0 w-screen h-svh flex items-center justify-center'>
        <Popup className='max-w-md add-folder-popup'>
          <div className='font-bold'>New folder</div>
          <input autoFocus className='border rounded mt-4 py-1 px-2 text-sm w-full' value={newFolderInput} onInput={(e: React.ChangeEvent<HTMLInputElement>) => setNewFolderInput(e.target.value)} />
          <div className='flex justify-end text-sm mt-8'>
            <button onClick={addFolder} className='font-bold bg-emerald-500 p-1 px-4 rounded active:bg-emerald-600 disabled:bg-gray-400' disabled={newFolderInput.length === 0}>Create</button>
            <button onClick={() => setIsAddingFolder(false)} className='font-bold bg-gray-600 p-1 px-4 rounded ml-4 active:bg-gray-700'>Cancel</button>
          </div>
        </Popup>
      </div>}
    </>
  )
}

export default ResourceFolderButton